import qs from 'qs';
import { z } from 'zod';

import { SortDirection } from '../../../components/table/sortable-header';
import { positiveInteger } from '../../../lib/url/hooks/use-queries-control';
import { FitlersAPIData } from '../../../queries/rewards';

export const createHistoryDetailParamParser = () => {
	return [
		['page', 'sortBy', 'sortDirection'],
		{
			defaultValue: {
				page: '1',
				sortBy: 'created_at',
				sortDirection: SortDirection.DESC,
			},
			allowedValues: {
				sortBy: ['created_at'],
				sortDirection: [SortDirection.ASC, SortDirection.DESC],
			},
			transforms: {
				page: positiveInteger,
			},
		},
	] as const;
};

export const defaultCollectionKey = (collections: FitlersAPIData['collections']) => {
	if (!collections || collections.length === 0) return 'all';
	return collections[0].key;
};

/**
 * Creates a Zod schema for validating rewards index params with dynamic collection and subcategory validation.
 * Ensures that filters.collection is always a valid string from the allowed collections.
 */
const DEFAULT_COLLECTION_KEY = 'all';

export const createRewardsIndexParamsSchema = (
	{
		allowedCollectionKeys,
		allowedSubcategories,
	}: {
		allowedCollectionKeys: string[];
		allowedSubcategories: string[];
	} = { allowedCollectionKeys: [], allowedSubcategories: [] }
) => {
	return z
		.object({
			q: z.string().optional().nullable().catch(undefined),
			filters: z
				.object({
					// Collection must be a string and one of the allowed keys
					// If invalid or missing, defaults to the first available collection
					collection: z
						.union([
							z.string(),
							// If somehow an array is passed, take the first element
							z.array(z.string()).transform((arr) => arr[0] || DEFAULT_COLLECTION_KEY),
						])
						.refine((key) => allowedCollectionKeys.includes(key), {
							message: 'Invalid collection key',
						})
						.catch(DEFAULT_COLLECTION_KEY)
						.default(DEFAULT_COLLECTION_KEY),
					// Coerce single values to arrays for consistency
					price: z
						.union([z.string().transform((s) => [s]), z.array(z.string())])
						.optional()
						.catch(undefined),
					type: z
						.union([z.string().transform((s) => [s]), z.array(z.string())])
						.optional()
						.catch(undefined),
					category: z
						.union([z.string().transform((s) => [s]), z.array(z.string())])
						.optional()
						.catch(undefined),
					// Subcategory filter - validated against allowed values if provided
					subCategory: z
						.string()
						.refine((key) => allowedSubcategories.length === 0 || allowedSubcategories.includes(key), {
							message: 'Invalid subcategory',
						})
						.optional()
						.catch(undefined),
					// Stored as points in the URL; converted to USD cents at the API boundary
					minPricePoints: z
						.union([z.string().transform((val) => Number(val)), z.number()])
						.refine((val) => Number.isFinite(val))
						.optional()
						.catch(undefined),
					maxPricePoints: z
						.union([z.string().transform((val) => Number(val)), z.number()])
						.refine((val) => Number.isFinite(val))
						.optional()
						.catch(undefined),
				})
				.default({ collection: DEFAULT_COLLECTION_KEY }),
			pageNumber: z
				.union([z.string().transform((val) => positiveInteger(val)), z.number().transform((val) => Math.max(1, Math.floor(val)))])
				.catch(1)
				.default(1),
		})
		.catch({ filters: { collection: DEFAULT_COLLECTION_KEY }, pageNumber: 1 });
};

export type ParsedRewardsIndexParams = z.infer<ReturnType<typeof createRewardsIndexParamsSchema>>;

/**
 * Maps URL/query index params to rewards catalog API params.
 * Point-range filters stay in the URL as points; convert to USD cents for the API
 * so the backend can remain points-agnostic.
 */
export const toRewardsApiParams = (
	params: ParsedRewardsIndexParams,
	{ hasSearchImprovements, exchangeRate }: { hasSearchImprovements: boolean; exchangeRate: number }
) => {
	const { minPricePoints, maxPricePoints, price, ...restFilters } = params.filters;

	return {
		...params,
		filters: {
			...restFilters,
			// Legacy price buckets conflict with the points range; omit them when the new filter is on
			...(!hasSearchImprovements && price != null ? { price } : {}),
			...(hasSearchImprovements && {
				minPriceCents: minPricePoints != null ? Math.round((minPricePoints / exchangeRate) * 100) : undefined,
				maxPriceCents: maxPricePoints != null ? Math.round((maxPricePoints / exchangeRate) * 100) : undefined,
			}),
		},
	};
};

/**
 * Parses and validates index params from a URL search string.
 * Guarantees that filters.collection will always be a valid string.
 */
export const parseIndexParamsFromSearch = ({
	search,
	collections,
	subCategories,
}: {
	search: string;
	collections: FitlersAPIData['collections'];
	subCategories: string[];
}) => {
	const allowedKeys = collections.map((c) => c.key);
	const schema = createRewardsIndexParamsSchema({ allowedCollectionKeys: allowedKeys, allowedSubcategories: subCategories });

	const paramsObject = qs.parse(search, { ignoreQueryPrefix: true });
	const indexParams = schema.parse(paramsObject);

	return indexParams;
};

/**
 * Extracts browse context from query params for analytics tracking.
 * Returns context about user's current browsing state (search, filters, collection).
 */
export const getBrowseContext = (params: ParsedRewardsIndexParams) => {
	return {
		hasSearch: !!params.q,
		searchQuery: params.q || undefined,
		collection: params.filters.collection,
		isFilteringByBudget: params.filters.price?.includes('in_budget') ?? false,
		isFilteringByType: (params.filters.type?.length ?? 0) > 0,
		typeFilters: params.filters.type,
		isFilteringByCategory: (params.filters.category?.length ?? 0) > 0,
		categoryFilters: params.filters.category,
		subCategory: params.filters.subCategory,
		pageNumber: params.pageNumber,
	};
};
